import { useCallback } from 'react';
import { convertToDisplayName } from '../helpers';
import * as Yup from 'yup';

type YupError = Yup.ValidationError;

/**
 * formats validation text from yup for form components.
 * @note please add custom validation rules when necessary to a switch branch.
 * @param error - YupError from React-Hook-Form
 * @returns formatted helper text
 */
const formatErrorMessage = (error: YupError['inner'][number]) => {
    const formattedErrorMessage = error.path
        ? error.message.replace(error.path, convertToDisplayName(error.path))
        : error.message;

    return formattedErrorMessage;
};

export const useYupValidationResolver = (
    validationSchema: Yup.AnyObjectSchema
) =>
    useCallback(
        async (data: any) => {
            try {
                const values = await validationSchema.validate(data, {
                    abortEarly: false,
                });

                return {
                    values,
                    errors: {},
                };
            } catch (errors: any) {
                const yupErrors = (errors as YupError).inner.map((e) => ({
                    ...e,
                    message: formatErrorMessage(e),
                }));

                const formattedYupErrors = {
                    values: {},
                    errors: yupErrors.reduce((allErrors, currentError) => {
                        // format currentError field to yup standard to match yup format
                        const currentErrorPath = currentError.path
                            ?.replace(/(\[\d+\]+)/g, '.$1')
                            .replace(/(\[|\])/g, '');

                        return {
                            ...allErrors,
                            [currentErrorPath ?? '']: {
                                type: currentError.type ?? 'validation',
                                message: currentError.message,
                            },
                        };
                    }, {}),
                };

                return formattedYupErrors;
            }
        },
        [validationSchema]
    );
